import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import useGeolocation from "../hooks/useGeolocation";
import { saveNetworks, getCachedNetworks, savePoles, getCachedPoles, getCachedPolesUpdatedAt, addToOutbox, countPendingOutbox } from "../utils/offlineDb";
import normalize, { fuzzyIncludes } from "../utils/normalize";
import { syncOutbox } from "../utils/syncEngine";
import { compressImage } from "../utils/compressImage";
import { localVerify } from "../utils/localVerify";
import { parseCoordinate } from "../utils/coords";

// все "полевые" ключи из pole_checkups (кроме системных)
const CHECK_ITEMS = [
  ["foundation_cond", "Tayanch fundamenti holati"],
  ["foundation_nuts_tight", "Tayanch fundamentidagi gaykalar to'liq qotirilganligi"],
  ["base_washout", "Tayanch ostini suv yuvish, ochilib qolish holati"],
  ["soil_protect_obval", "Tayanch osti, fundamentlari tuproq bilan himoyalanganligi; (obvalovka)"],
  ["earthing_kz_cond", "Tayanch yerlagichlari holati(KZ)"],
  ["concrete_base_protect", "J/B tayanch holati, ostki qismi tuproq bilan himoyalanganligi"],
  ["pole_tilt", "Tayanchni qiyshaygan, og'ishma holati"],
  ["dispatcher_marks", "Dispetcherlik yozuvlari, nomlanishi  mavjudligi"],
  ["angle_iron_cond", "Tayanch ugolniklari holati"],
  ["nuts_welded", "Tayanch gaykalarining payvandlanganligi"],
  ["bolts_thread_punched", "Tayanchlardagi boltlarning rezbalari kerno qilinganligi"],
  ["angle_iron_welded", "Tayanchlardagi ugolniklarni bir-biriga payvandlanganligi (o'g'irlikni oldini olish)"],
  ["factory_welds_cond", "Ishlab chiqaruvchi tomonidan payvandlangan choklarining holati (korruziyaga uchrashi, yemirilganligi....)"],
  ["leg_fit_to_found", "Metall tayanch oyog'ining fundament bilan oralig'i jipsligi (bo'shlig'i)"],
  ["guy_anbolts_cond", "Tayanch tortqichlari va AN boltlarini holati"],
  ["cotter_pins", "Shplintlar mavjudligi"],
  ["insulators_cond", "Izolyatorlar holati va ularning to'g'riligi, og'ishma holati"],
  ["conductors_cond", "Simlarni holati"],
  ["shunt_cond", "Shunt holati"],
  ["sas_present", "Shleyflardagi hamda tayanchlar oralig'ida SASlar mavjudligi"],
  ["thermovision_temp", "Boltli va pressli birikmalardagi teplovizor nazoratida harorat ko'rsatgichi"],
  ["temp_sas_c", "САС (С°)"],
  ["temp_nas_c", "НАС (С°)"],
  ["pa_clamps_cond", "Boltli birikmalar (PA qisqichlar) holati"],
  ["thermoweld_present", "Termopayka mavjudligi"],
  ["damper_gvn_cond", "Simdagi so'ndirg'ich holati; (GVN)"],
  ["spacers_cond", "Rasporkalar holati"],
  ["gzt_present", "Chaqmoqqa qarshi tros (GZT) mavjudligi"],
  ["gzt_cond", "Chaqmoqqa qarshi tros holati"],
  ["spark_gap_cond", "Uchqun oraliq holati; (Искровой промежуток)"],
  ["gzt_earthing_cond", "Chaqmoqqa qarshi tros yerlanganlik holati"],
  ["vols_cond", "Optik tolali aloqa tarmog'i (VOLS) holati"],
  ["corridor_trees", "Muhofaza hududida daraxtlar mavjudligi"],
  ["corridor_reeds", "Muhofaza hududida qamish"],
  ["corridor_wheat", "Muhofaza hududida bug'doy maydonlari mavjudligi"],
  ["corridor_buildings", "Muhofaza hududda bino, inshoatlar borligi"],
  ["clearance_ok", "Sim bilan yer, daraxt, bino orasidagi masofa holati"],
  ["bird_guard_qqt", "QQT (qushlarga qarshi to'siq) mavjudligi"],
  ["cross_road", "Yo'l bilan kesishmalar mavjudligi"],
  ["cross_powerline", "Elektr tarmog'i bilan kesishmalar mavjudligi"],
  ["cross_river", "Daryo va hokazo bilan kesishmalar mavjudligi"],
  ["winter_ice", "Qish mavsumida sim, trosdagi muzlamalar mavjudligi"],
];

const CHECK_KEYS_SET = new Set(CHECK_ITEMS.map(([k]) => k));

const SHORT_CHECK_KEYS = new Set([
  "base_washout",
  "soil_protect_obval",
  "pole_tilt",
  "dispatcher_marks",
  "angle_iron_cond",
  "insulators_cond",
  "conductors_cond",
  "shunt_cond",
  "sas_present",
  "pa_clamps_cond",
  "damper_gvn_cond",
  "gzt_cond",
  "vols_cond",
  "corridor_trees",
  "corridor_buildings",
  "clearance_ok",
  "bird_guard_qqt",
  "cross_road",
  "cross_powerline",
  "cross_river",
]);

const CHECK_LABELS = Object.fromEntries(CHECK_ITEMS);

function cryptoUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// YYYY-MM-DDTHH:mm в Asia/Tashkent — формат, который сервер умеет парсить как +05:00
function tashkentNowLocalInput() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

// "YYYY-MM-DDTHH:mm" → "HH:mm"
function timeFromLocalInput(localInput) {
  if (!localInput) return null;
  return localInput.split("T")[1] || null;
}

function reasonText(r) {
  if (!r) return "—";
  const v = String(r).toLowerCase();
  if (v === "accuracy_bad") return "GPS аниқлиги паст";
  if (v === "too_far") return "таянчдан узоқ";
  if (v === "no_gps") return "геолокация йўқ";
  if (v === "no_pole_coords") return "таянчда координаталар йўқ (БД да)";
  if (v === "verify_api_error") return "текширув хизмати хатоси";
  return String(r);
}

function statusText(s) {
  const v = String(s || "").toLowerCase();
  if (v === "verified") return "Тасдиқланди";
  if (v === "failed") return "Мувофиқ эмас";
  if (v === "pending") return "Текширилмаган";
  if (v === "skipped_offline") return "Офлайн";
  return v || "—";
}

function normalizeList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.items)) return json.items;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function getDefaultAnswers() {
  const obj = {};
  for (const [k] of CHECK_ITEMS) obj[k] = "yes";
  return obj;
}

export default function AddPoleCheckup() {
  const geo = useGeolocation();

  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  // frozenPos: позиция зафиксирована когда инспектор стоит у опоры.
  // Используется при сохранении вместо повторного geo.refresh().
  // Поле capturedAt — строка "YYYY-MM-DDTHH:mm" в ташкентском времени.
  const [frozenPos, setFrozenPos] = useState(null);

  const [outboxCount, setOutboxCount] = useState(0);
  const [isSyncingUI, setIsSyncingUI] = useState(false);

  const [networks, setNetworks] = useState([]);
  const [networkSearch, setNetworkSearch] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState(null);

  const [poles, setPoles] = useState([]);
  const [polesLoading, setPolesLoading] = useState(false);
  const [poleSearch, setPoleSearch] = useState("");
  const [selectedPole, setSelectedPole] = useState(null);
  // признак что полюсы загружены из кэша, а не с сервера
  const [polesFromCache, setPolesFromCache] = useState(false);
  // timestamp (ms) последнего кэширования опор для выбранной линии
  const [polesCachedAt, setPolesCachedAt] = useState(null);
  // идёт сохранение в кэш
  const [cachingPoles, setCachingPoles] = useState(false);

  const [answers, setAnswers] = useState(() => getDefaultAnswers());
  const [reasons, setReasons] = useState({});
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);

  const [verify, setVerify] = useState({
    status: "pending",
    reason: null,
    distance_m: null,
  });
  const [verifyLoading, setVerifyLoading] = useState(false);

  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const [checkupType, setCheckupType] = useState("short");
  const activeCheckItems = useMemo(() => {
    if (checkupType === "short") return CHECK_ITEMS.filter(([k]) => SHORT_CHECK_KEYS.has(k));
    return CHECK_ITEMS;
  }, [checkupType]);

  // Второй сотрудник
  const [twoInspectors, setTwoInspectors] = useState(() => {
    return localStorage.getItem("checkup_two_inspectors") === "true";
  });
  const [inspector2, setInspector2] = useState(() => {
    try {
      const s = localStorage.getItem("checkup_inspector2");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [inspector2Search, setInspector2Search] = useState("");
  const [inspector2Results, setInspector2Results] = useState([]);
  const [inspector2Loading, setInspector2Loading] = useState(false);

  // Комментарии к yes пунктам
  const [yesComments, setYesComments] = useState({});

  // Аварийный
  const [isEmergency, setIsEmergency] = useState(false);

  // ─── Утилиты ────────────────────────────────────────────────────────────────

  const refreshOutboxCount = useCallback(async () => {
    try {
      const count = await countPendingOutbox();
      setOutboxCount(count);
    } catch (e) {
      console.error("refreshOutboxCount:", e);
    }
  }, []);

  const triggerSync = useCallback(async () => {
    if (!isOnline || isSyncingUI) return;
    setIsSyncingUI(true);
    try {
      await syncOutbox();
    } finally {
      setIsSyncingUI(false);
      await refreshOutboxCount();
    }
  }, [isOnline, isSyncingUI, refreshOutboxCount]);

  // ─── Effects ────────────────────────────────────────────────────────────────

  // online / offline
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // При появлении сети — запускаем синк в фоне
  useEffect(() => {
    if (!isOnline) return;
    triggerSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Счётчик очереди при загрузке
  useEffect(() => {
    refreshOutboxCount();
  }, [refreshOutboxCount]);

  // Загрузка линий: онлайн → сервер + кэш; офлайн → только кэш
  useEffect(() => {
    (async () => {
      if (navigator.onLine) {
        try {
          const res = await apiFetch("/api/network", { cache: "no-store" });
          const list = await res.json();
          const arr = Array.isArray(list) ? list : [];
          setNetworks(arr);
          await saveNetworks(arr);
        } catch (e) {
          console.error("networks fetch failed, using cache:", e);
          const cached = await getCachedNetworks();
          setNetworks(cached);
        }
      } else {
        const cached = await getCachedNetworks();
        setNetworks(cached);
      }
    })();
  }, []);

  // Первичное получение GPS
  useEffect(() => {
    (async () => {
      const p = await geo.refresh();
      if (p) {
        setFrozenPos({ ...p, capturedAt: tashkentNowLocalInput() });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Поиск второго инспектора (дебаунс 400ms)
  useEffect(() => {
    if (!twoInspectors || inspector2Search.trim().length < 2) {
      setInspector2Results([]);
      return;
    }
    const timer = setTimeout(async () => {
      setInspector2Loading(true);
      try {
        const res = await apiFetch(`/api/pole-checkups/inspectors?q=${encodeURIComponent(inspector2Search.trim())}`);
        const data = await res.json();
        setInspector2Results(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("inspector search:", e);
        setInspector2Results([]);
      } finally {
        setInspector2Loading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [inspector2Search, twoInspectors]);

  // ─── Геолокация ─────────────────────────────────────────────────────────────

  // Обновляет GPS и фиксирует позицию. Вызывается кнопкой и при выборе опоры.
  async function refreshGeo() {
    const p = await geo.refresh();
    if (!p) return null;
    const frozen = { ...p, capturedAt: tashkentNowLocalInput() };
    setFrozenPos(frozen);
    return frozen;
  }

  // Верификация: онлайн → сервер, офлайн → локально (Haversine)
  async function doVerify(pole, pos) {
    if (!pole || !pos) return;
    if (navigator.onLine) {
      await verifyNow(pole.id, pos);
    } else {
      const poleLat = parseCoordinate(pole.latitude, "lat");
      const poleLng = parseCoordinate(pole.longitude, "lng");
      const result = localVerify({
        lat: pos.lat,
        lng: pos.lng,
        acc: pos.acc,
        poleLat,
        poleLng,
      });
      setVerify({
        status: result.status,
        reason: result.reason,
        distance_m: result.distance_m,
      });
    }
  }

  // ─── Линии / Опоры ──────────────────────────────────────────────────────────

  const filteredNetworks = useMemo(() => {
    if (networkSearch.trim().length < 2) return [];
    return networks
      .filter((n) => {
        const name = normalize(String(n?.name || n?.slug || ""));
        const city = normalize(String(n?.city?.name || ""));
        return fuzzyIncludes(name, networkSearch) || fuzzyIncludes(city, networkSearch);
      })
      .slice(0, 20);
  }, [networks, networkSearch]);

  const filteredPoles = useMemo(() => {
    const q = poleSearch.trim().toLowerCase();
    if (!selectedNetwork?.id || q.length < 1) return [];
    return poles
      .filter((p) => {
        const poleName = String(p?.pole_name ?? p?.poleName ?? p?.name ?? "").toLowerCase();
        return poleName.includes(q);
      })
      .slice(0, 30);
  }, [poles, poleSearch, selectedNetwork]);

  async function loadPolesForNetwork(network) {
    setPoles([]);
    setSelectedPole(null);
    setPoleSearch("");
    setPolesFromCache(false);
    setPolesCachedAt(null);
    setVerify({ status: "pending", reason: null, distance_m: null });

    if (!network?.id) return;

    setPolesLoading(true);
    setApiError("");

    try {
      if (navigator.onLine) {
        const res = await apiFetch(`/api/poles-lite?network_id=${network.id}`, {
          cache: "no-store",
        });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(t || "Failed to load poles-lite");
        }
        const json = await res.json();
        const list = normalizeList(json);
        setPoles(list);
        setPolesFromCache(false);
        // Показываем дату уже существующего кэша (если есть)
        const cachedAt = await getCachedPolesUpdatedAt(network.id);
        setPolesCachedAt(cachedAt);
      } else {
        const cached = await getCachedPoles(network.id);
        const cachedAt = await getCachedPolesUpdatedAt(network.id);
        if (cached.length === 0) {
          setApiError(
            "Офлайн: бу линия сақланмаган.\n" +
            "Интернет борлигида линияни танланг, сўнг «Офлайн учун сақлаш» тугмасини босинг."
          );
        }
        setPoles(cached);
        setPolesFromCache(cached.length > 0);
        setPolesCachedAt(cachedAt);
      }
    } catch (e) {
      console.error("loadPolesForNetwork:", e);
      // При любой ошибке сети — пробуем кэш
      try {
        const cached = await getCachedPoles(network.id);
        const cachedAt = await getCachedPolesUpdatedAt(network.id);
        if (cached.length > 0) {
          setPoles(cached);
          setPolesFromCache(true);
          setPolesCachedAt(cachedAt);
        } else {
          setApiError("Таянчларни юклаб бўлмади.");
          setPoles([]);
        }
      } catch {
        setApiError("Таянчларни юклаб бўлмади.");
        setPoles([]);
      }
    } finally {
      setPolesLoading(false);
    }
  }

  // Явное сохранение линии в кэш по кнопке пользователя
  async function cacheNetworkForOffline() {
    if (!selectedNetwork?.id || !isOnline) return;
    setCachingPoles(true);
    setApiError("");
    try {
      const res = await apiFetch(`/api/poles-lite?network_id=${selectedNetwork.id}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Failed to load poles-lite");
      const json = await res.json();
      const list = normalizeList(json);
      await savePoles(list);
      setPoles(list);
      setPolesFromCache(false);
      setPolesCachedAt(Date.now());
    } catch (e) {
      console.error("cacheNetworkForOffline:", e);
      setApiError("Сақлашда хато. Қайта уриниб кўринг.");
    } finally {
      setCachingPoles(false);
    }
  }

  // ─── Верификация (онлайн, сервер) ───────────────────────────────────────────

  async function verifyNow(poleId, userPos) {
    setVerifyLoading(true);
    try {
      const res = await apiFetch("/api/pole-checkups/verify", {
        method: "POST",
        body: {
          pole_id: poleId,
          latitude: userPos?.lat,
          longitude: userPos?.lng,
          accuracy_m: userPos?.acc,
        },
      });

      let data = null;
      try { data = await res.json(); } catch (_) { data = null; }

      if (!res.ok) {
        const next = { status: "failed", reason: "verify_api_error", distance_m: null };
        setVerify(next);
        return next;
      }

      const next = {
        status: data?.status || "failed",
        reason: data?.reason || null,
        distance_m: typeof data?.distance_m === "number" ? data.distance_m : null,
      };
      setVerify(next);
      return next;
    } catch (e) {
      console.error(e);
      const next = { status: "failed", reason: "verify_api_error", distance_m: null };
      setVerify(next);
      return next;
    } finally {
      setVerifyLoading(false);
    }
  }

  // ─── Чеклист ────────────────────────────────────────────────────────────────

  function setAnswer(key, value) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (value !== "no") {
      setReasons((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function toggleTwoInspectors(val) {
    setTwoInspectors(val);
    localStorage.setItem("checkup_two_inspectors", String(val));
    if (!val) {
      setInspector2(null);
      localStorage.removeItem("checkup_inspector2");
      setInspector2Search("");
      setInspector2Results([]);
    }
  }

  function selectInspector2(user) {
    setInspector2(user);
    localStorage.setItem("checkup_inspector2", JSON.stringify(user));
    setInspector2Search("");
    setInspector2Results([]);
  }

  function clearInspector2() {
    setInspector2(null);
    localStorage.removeItem("checkup_inspector2");
    setInspector2Search("");
    setInspector2Results([]);
  }

  // ─── Сброс формы ────────────────────────────────────────────────────────────

  function resetAfterSaveKeepNetwork() {
    setSelectedPole(null);
    setPoleSearch("");
    setAnswers(getDefaultAnswers());
    setReasons({});
    setYesComments({});
    setNotes("");
    setFiles([]);
    setFileInputKey((x) => x + 1);
    setVerify({ status: "pending", reason: null, distance_m: null });
    setVerifyLoading(false);
    setApiError("");
    setIsEmergency(false);
    setInspector2Search("");
    setInspector2Results([]);
    // inspector2 и twoInspectors НЕ сбрасываем — пользователь хочет сохранить выбор
  }

  // ─── Валидация ──────────────────────────────────────────────────────────────

  function validateBeforeSave() {
    if (!selectedNetwork?.id) return "Линияни танланг.";
    if (!selectedPole?.id) return "Таянчни танланг.";
    if (!frozenPos) return "Геолокацияни олиб бўлмади. «Геолокацияни янгилаш»ни босинг.";
    if (twoInspectors && !inspector2) return "Иккинчи инспекторни танланг.";

    for (const [k] of activeCheckItems) {
      if (answers[k] === "no") {
        const r = String(reasons?.[k] || "").trim();
        if (!r) return `Сабабни ёзинг: "${CHECK_LABELS[k] || k}"`;
      }
    }

    if (files.length > 10) return "Энг кўпи 10 та фото.";
    return null;
  }

  // ─── Сохранение ─────────────────────────────────────────────────────────────

  async function saveCheckup() {
    setApiError("");
    setSuccess("");

    const err = validateBeforeSave();
    if (err) return setApiError(err);

    setSaving(true);

    try {
      // frozenPos зафиксирован когда инспектор стоял у опоры
      const pos = frozenPos;

      // Общие поля для обоих путей
      const client_id = cryptoUUID();
      const performed_at = tashkentNowLocalInput();       // момент нажатия "Сохранить"
      const location_captured_at = pos.capturedAt;        // момент последнего geo.refresh()

      const negative = {};
      for (const [k] of activeCheckItems) {
        if (answers[k] === "no") negative[k] = String(reasons[k] || "").trim();
      }

      const fields = {};
      for (const [k] of CHECK_ITEMS) {
        if (checkupType === "short" && !SHORT_CHECK_KEYS.has(k)) {
          fields[k] = "";
        } else {
          fields[k] = answers[k] || "yes";
        }
      }

      if (isOnline) {
        // ── Онлайн: верификация на сервере, потом POST ──────────────────────
        const v = await verifyNow(selectedPole.id, pos);
        if (v.status !== "verified") {
          const dist = typeof v.distance_m === "number"
            ? `${Math.round(v.distance_m * 10) / 10} м`
            : "—";
          setApiError(
            `Жойлашув мос келмади ёки GPS аниқлиги етарли эмас.\n` +
            `Масофа: ${dist} · Сабаб: ${reasonText(v.reason)}\n` +
            `GPS'ни янгилаб, қайта уриниб кўринг.`
          );
          return;
        }

        const basePayload = {
          client_id,
          pole_id: String(selectedPole.id),
          performed_at,
          checkup_type: checkupType,
          location_captured_at,
          latitude: String(pos.lat),
          longitude: String(pos.lng),
          accuracy_m: String(pos.acc ?? ""),
          is_offline: "false",
          negative_reasons: JSON.stringify(negative),
          yes_comments: JSON.stringify(yesComments),
          inspector_2_id: twoInspectors && inspector2 ? String(inspector2.id) : "",
          notes: notes ? String(notes).trim() : "",
          is_emergency: isEmergency ? "true" : "false",
          ...fields,
        };

        const form = new FormData();
        for (const [k, v0] of Object.entries(basePayload)) {
          const val = v0 === undefined || v0 === null ? "" : String(v0);
          if (
            val.trim() === "" &&
            !CHECK_KEYS_SET.has(k) &&
            !["latitude", "longitude", "accuracy_m"].includes(k)
          ) continue;
          form.append(k, val);
        }
        files.forEach((f) => form.append("files", f));

        const res = await apiFetch("/api/pole-checkups", { method: "POST", body: form });
        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || "Failed");
        }

        setSuccess("Кўрик сақланди ✅");
        resetAfterSaveKeepNetwork();
        setTimeout(() => setSuccess(""), 2000);

      } else {
        // ── Офлайн: проверяем верификацию перед сохранением ─────────────────
        if (verify.status !== "verified") {
          const dist = typeof verify.distance_m === "number"
            ? `${Math.round(verify.distance_m * 10) / 10} м`
            : "—";
          setApiError(
            `Жойлашув мос келмади.\n` +
            `Масофа: ${dist} · Сабаб: ${reasonText(verify.reason)}\n` +
            (verify.reason === "no_pole_coords"
              ? "Таянчнинг координаталари базада мавжуд эмас. Онлайн режимда қайта уриниб кўринг."
              : "GPS'ни янгилаб, таянч ёнида туриб қайта уриниб кўринг.")
          );
          return;
        }

        // ── Офлайн: сжимаем фото, кладём в очередь ─────────────────────────
        const photos = [];
        for (const file of files) {
          try {
            const blob = await compressImage(file);
            const data = await blob.arrayBuffer();
            photos.push({ name: file.name, type: "image/jpeg", data });
          } catch (e) {
            console.warn("compress failed, storing original:", e);
            try {
              const data = await file.arrayBuffer();
              photos.push({ name: file.name, type: file.type, data });
            } catch (e2) {
              console.error("failed to store photo:", e2);
            }
          }
        }

        await addToOutbox({
          client_id,
          pole_id: String(selectedPole.id),
          performed_at,
          checkup_type: checkupType,
          location_captured_at,
          latitude: String(pos.lat),
          longitude: String(pos.lng),
          accuracy_m: String(pos.acc ?? ""),
          is_offline: "true",
          negative_reasons: JSON.stringify(negative),
          yes_comments: JSON.stringify(yesComments),
          inspector_2_id: twoInspectors && inspector2 ? String(inspector2.id) : "",
          notes: notes ? String(notes).trim() : "",
          is_emergency: isEmergency ? "true" : "false",
          ...fields,
          photos,
        });

        const newCount = await countPendingOutbox();
        setOutboxCount(newCount);
        setSuccess(`Офлайн сақланди ✅ Навбатда: ${newCount} та кўрик`);
        resetAfterSaveKeepNetwork();
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (e) {
      console.error(e);
      setApiError("Сақлашда хато. Қайта уриниб кўринг.");
    } finally {
      setSaving(false);
    }
  }

  // ─── Badge helpers ───────────────────────────────────────────────────────────

  function badgeForOnline() {
    return isOnline
      ? "checkup-add__badge checkup-add__badge--ok"
      : "checkup-add__badge checkup-add__badge--warn";
  }

  function badgeForVerify() {
    if (verifyLoading) return "checkup-add__badge";
    if (verify.status === "verified") return "checkup-add__badge checkup-add__badge--ok";
    if (verify.status === "failed") return "checkup-add__badge checkup-add__badge--bad";
    if (verify.status === "skipped_offline") return "checkup-add__badge checkup-add__badge--warn";
    return "checkup-add__badge";
  }

  function verifyLineText() {
    const dist =
      typeof verify.distance_m === "number"
        ? `${Math.round(verify.distance_m * 10) / 10} м`
        : "—";
    return `Ҳолат: ${verifyLoading ? "..." : statusText(verify.status)} · Масофа: ${dist} · Сабаб: ${reasonText(verify.reason)}`;
  }

  // ─── JSX ────────────────────────────────────────────────────────────────────

  const selectedNetworkTitle = selectedNetwork?.name || selectedNetwork?.slug || "—";
  const selectedPoleTitle =
    selectedPole?.name ||
    selectedPole?.pole_name ||
    selectedPole?.poleName ||
    (selectedPole ? `Таянч #${selectedPole.id}` : "—");
  const gpsSource = frozenPos || geo.pos;
  const gpsAccuracyLabel = gpsSource ? `${Math.round(gpsSource.acc)} м` : "—";
  const capturedTimeLabel = frozenPos?.capturedAt
    ? timeFromLocalInput(frozenPos.capturedAt)
    : "—";
  const verifyDistanceLabel =
    typeof verify.distance_m === "number"
      ? `${Math.round(verify.distance_m * 10) / 10} м`
      : "—";
  const verifyStatusLabel = verifyLoading
    ? "Текширилмоқда..."
    : verify.status === "verified"
    ? "Тасдиқланди"
    : verify.status === "failed"
    ? "Мувофиқ эмас"
    : verify.status === "skipped_offline"
    ? "Офлайн"
    : "Текширилмаган";
  const cacheStatusLabel = selectedNetwork
    ? polesLoading
      ? "Таянчлар юкланмоқда"
      : polesCachedAt
      ? polesFromCache
        ? `Кешдан юкланди · ${poles.length} та`
        : `Офлайн учун сақланган · ${poles.length} та`
      : isOnline
      ? "Офлайн кеш тайёр эмас"
      : "Офлайн кеш йўқ"
    : "Линия танланмаган";
  const checklistAnswered = activeCheckItems.filter(([key]) => Boolean(answers[key])).length;
  const checklistNoCount = activeCheckItems.filter(([key]) => answers[key] === "no").length;
  const missingReasonCount = activeCheckItems.filter(
    ([key]) => answers[key] === "no" && !String(reasons?.[key] || "").trim()
  ).length;
  const yesCommentCount = activeCheckItems.filter(
    ([key]) => answers[key] === "yes" && String(yesComments?.[key] || "").trim()
  ).length;

  return (
    <Layout>
      <div className="checkup-add-page">
        <div className="container">
          <div className="checkup-add">
            <header className="checkup-add__hero">
              <div className="checkup-add__hero-main">
                <h1 className="checkup-add__title">Таянч кўриги</h1>
              </div>

              <div className="checkup-add__status-grid" aria-label="Операцион ҳолат">
                <span className={badgeForOnline()}>{isOnline ? "Online" : "Offline"}</span>
                <span className={badgeForVerify()}>{verifyStatusLabel}</span>
                {outboxCount > 0 ? (
                  <button
                    type="button"
                    className="checkup-add__badge checkup-add__badge--warn checkup-add__badge--action"
                    onClick={isOnline ? triggerSync : undefined}
                    disabled={!isOnline || isSyncingUI}
                    title={isOnline ? "Ҳозир юбориш" : "Интернет пайдо бўлганда автоматик юборилади"}
                  >
                    {isSyncingUI ? "Юборилмоқда..." : `Навбатда: ${outboxCount}`}
                  </button>
                ) : (
                  <span className="checkup-add__badge">Навбат тоза</span>
                )}
              </div>
            </header>

          <div className="checkup-add__ops-strip">
            <div className="checkup-add__ops-item">
              <span>Объект</span>
              <strong>
                {selectedPole
                  ? selectedPoleTitle
                  : selectedNetwork
                  ? selectedNetworkTitle
                  : "Танланмаган"}
              </strong>
            </div>
            <div className="checkup-add__ops-item">
              <span>Кеш</span>
              <strong>{cacheStatusLabel}</strong>
            </div>
            <div className="checkup-add__ops-item">
              <span>GPS</span>
              <strong>{gpsAccuracyLabel}</strong>
            </div>
            <div className="checkup-add__ops-item">
              <span>Текширув</span>
              <strong>{verifyStatusLabel}</strong>
            </div>
            <div className="checkup-add__ops-item">
              <span>Фото</span>
              <strong>{files.length}/10</strong>
            </div>
          </div>

          <div className="checkup-add__mobile-hint">
            Офлайн режимда кўрик навбатга сақланади. Интернет пайдо бўлганда синхронлашади.
          </div>

          <div className="checkup-add__header">
            <h1 className="checkup-add__title">Таянч кўриги</h1>
            <div className="checkup-add__status">
              <span className={badgeForOnline()}>{isOnline ? "Online" : "Offline"}</span>
              <span className={badgeForVerify()}>
                {verifyLoading ? "Текширилмоқда..." : statusText(verify.status)}
              </span>
              {outboxCount > 0 && (
                <span
                  className="checkup-add__badge checkup-add__badge--warn"
                  style={{ cursor: isOnline ? "pointer" : "default" }}
                  onClick={isOnline ? triggerSync : undefined}
                  title={isOnline ? "Ҳозир юбориш" : "Интернет пайдо бўлганда автоматик юборилади"}
                >
                  {isSyncingUI ? "Юборилмоқда..." : `Навбатда: ${outboxCount}`}
                </span>
              )}
            </div>
          </div>

          {/* 1) Кўрик объекти */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">Кўрик объекти</div>
            <div className="checkup-add__subsection-title">Линия</div>

            <div className="checkup-add__picker">
              <input
                className="checkup-add__search-input"
                placeholder="Линия номини киритинг (камида 2 та белги)..."
                value={networkSearch}
                onChange={(e) => setNetworkSearch(e.target.value)}
              />

              {networkSearch.trim().length >= 2 && (
                <div className="checkup-add__dropdown">
                  {filteredNetworks.length === 0 ? (
                    <div className="checkup-add__dropdown-empty">Ҳеч нарса топилмади</div>
                  ) : (
                    filteredNetworks.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className="checkup-add__dropdown-item"
                        onClick={async () => {
                          setSelectedNetwork(n);
                          setNetworkSearch("");
                          await loadPolesForNetwork(n);
                        }}
                      >
                        <div className="checkup-add__dropdown-name">{n.name || n.slug || "—"}</div>
                        <div className="checkup-add__dropdown-meta">{n.city?.name || "—"}</div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {selectedNetwork && (
                <div className="checkup-add__selected">
                  <div className="checkup-add__selected-title">
                    Танланди: <span>{selectedNetwork.name || selectedNetwork.slug || "—"}</span>
                    <span className="checkup-add__selected-meta">
                      {selectedNetwork.city?.name ? ` · ${selectedNetwork.city.name}` : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {isOnline && (
                      <button
                        type="button"
                        className="checkup-add__btn"
                        disabled={cachingPoles}
                        onClick={cacheNetworkForOffline}
                        title="Таянчларни офлайн учун сақлаш"
                      >
                        {cachingPoles
                          ? "Сақланмоқда..."
                          : polesCachedAt
                          ? "Кэшни янгилаш"
                          : "Офлайн учун сақлаш"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="checkup-add__link"
                      onClick={() => {
                        setSelectedNetwork(null);
                        setPoles([]);
                        setSelectedPole(null);
                        setPoleSearch("");
                        setPolesFromCache(false);
                        setPolesCachedAt(null);
                        setVerify({ status: "pending", reason: null, distance_m: null });
                      }}
                    >
                      Тозалаш
                    </button>
                  </div>
                </div>
              )}

              {/* Статус кэша */}
              {selectedNetwork && !polesLoading && polesCachedAt && (
                <div style={{ color: "#2e7d32", fontSize: 12, marginTop: 4 }}>
                  {polesFromCache
                    ? `Офлайн · кэшдан юкланди (${poles.length} та таянч) · ${new Date(polesCachedAt).toLocaleDateString("uz-UZ")}`
                    : `✓ Офлайн учун сақланди (${poles.length} та таянч) · ${new Date(polesCachedAt).toLocaleDateString("uz-UZ")}`}
                </div>
              )}
              {selectedNetwork && !polesLoading && !polesCachedAt && !isOnline && (
                <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                  Сақланмаган. Интернет борлигида «Офлайн учун сақлаш» тугмасини босинг.
                </div>
              )}
              {polesLoading && <div className="checkup-add__hint">Таянчлар юкланмоқда...</div>}
            </div>
          </div>

          {/* 2) Таянч */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">Таянч</div>

            {!selectedNetwork ? (
              <div className="checkup-add__hint">Аввал линияни танланг.</div>
            ) : (
              <div className="checkup-add__picker">
                <input
                  className="checkup-add__search-input"
                  placeholder="Таянч номини киритиб қидиринг..."
                  value={poleSearch}
                  onChange={(e) => setPoleSearch(e.target.value)}
                />

                {poleSearch.trim().length >= 1 && (
                  <div className="checkup-add__dropdown">
                    {filteredPoles.length === 0 ? (
                      <div className="checkup-add__dropdown-empty">Ҳеч нарса топилмади</div>
                    ) : (
                      filteredPoles.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="checkup-add__dropdown-item"
                          onClick={async () => {
                            setSelectedPole(p);
                            setPoleSearch("");
                            // Обновляем GPS при выборе опоры и сразу верифицируем
                            const pos = await refreshGeo();
                            const posToVerify = pos || frozenPos;
                            if (posToVerify) await doVerify(p, posToVerify);
                          }}
                        >
                          <div className="checkup-add__dropdown-name">
                            {p.name || `Таянч #${p.id}`}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {selectedPole && (
                  <div className="checkup-add__selected">
                    <div className="checkup-add__selected-title">
                      Танланди: <span>{selectedPole.name || `Таянч #${selectedPole.id}`}</span>
                      <span className="checkup-add__selected-meta">
                        {` · ID ${selectedPole.id}`}
                        {selectedPole.order_index ? ` · №${selectedPole.order_index}` : ""}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="checkup-add__link"
                      onClick={() => {
                        setSelectedPole(null);
                        setVerify({ status: "pending", reason: null, distance_m: null });
                      }}
                    >
                      Тозалаш
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3) GPS ва жойлашув */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">GPS ва жойлашув</div>

            <div className="checkup-add__geo-row">
              <div className="checkup-add__geo-meta">
                <div>
                  Аниқлик:{" "}
                  {frozenPos
                    ? `${Math.round(frozenPos.acc)} м`
                    : geo.pos
                    ? `${Math.round(geo.pos.acc)} м`
                    : "—"}
                </div>
                {frozenPos?.capturedAt && (
                  <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>
                    Олинган: {timeFromLocalInput(frozenPos.capturedAt)}
                  </div>
                )}
                {geo.error && <div style={{ marginTop: 6 }}>geo: {geo.error}</div>}
              </div>

              <button
                type="button"
                className="checkup-add__btn"
                disabled={geo.loading}
                onClick={async () => {
                  const pos = await refreshGeo();
                  if (selectedPole && pos) await doVerify(selectedPole, pos);
                }}
              >
                {geo.loading ? "Янгиланмоқда..." : "Геолокацияни янгилаш"}
              </button>
            </div>

            <div className="checkup-add__gps-grid">
              <div className="checkup-add__gps-card">
                <span>Аниқлик</span>
                <strong>{gpsAccuracyLabel}</strong>
              </div>
              <div className="checkup-add__gps-card">
                <span>Олинган вақт</span>
                <strong>{capturedTimeLabel}</strong>
              </div>
              <div className={`checkup-add__gps-card checkup-add__gps-card--${verify.status}`}>
                <span>Текширув</span>
                <strong>{verifyStatusLabel}</strong>
              </div>
              <div className="checkup-add__gps-card">
                <span>Масофа</span>
                <strong>{verifyDistanceLabel}</strong>
              </div>
            </div>

            <div className="checkup-add__verify-line">{verifyLineText()}</div>
          </div>

          {/* 4) Кўрик тури ва инспекторлар */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">Кўрик тури ва инспекторлар</div>

            <div className="checkup-add__segmented" role="group" aria-label="Кўрик тури">
              <label
                className={
                  "checkup-add__segment " + (checkupType === "short" ? "is-active" : "")
                }
              >
                <input
                  type="radio"
                  name="checkupType"
                  checked={checkupType === "short"}
                  onChange={() => setCheckupType("short")}
                />
                Қисман кўрикдан ўтказиш
              </label>

              <label
                className={
                  "checkup-add__segment " + (checkupType === "full" ? "is-active" : "")
                }
              >
                <input
                  type="radio"
                  name="checkupType"
                  checked={checkupType === "full"}
                  onChange={() => setCheckupType("full")}
                />
                Тўлиқ кўрикдан ўтказиш
              </label>
            </div>
          </div>

          {/* 5) Второй сотрудник */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">Хизматчилар сони</div>

            <div className="checkup-add__segmented checkup-add__segmented--compact" role="group" aria-label="Хизматчилар сони">
              <label
                className={
                  "checkup-add__segment " + (!twoInspectors ? "is-active" : "")
                }
              >
                <input
                  type="radio"
                  name="inspectorCount"
                  checked={!twoInspectors}
                  onChange={() => toggleTwoInspectors(false)}
                />
                1 та хизматчи
              </label>
              <label
                className={
                  "checkup-add__segment " + (twoInspectors ? "is-active" : "")
                }
              >
                <input
                  type="radio"
                  name="inspectorCount"
                  checked={twoInspectors}
                  onChange={() => toggleTwoInspectors(true)}
                />
                2 та хизматчи
              </label>
            </div>

            {twoInspectors && (
              <div className="checkup-add__picker">
                {inspector2 ? (
                  <div className="checkup-add__selected">
                    <div className="checkup-add__selected-title">
                      2-хизматчи:{" "}
                      <span>
                        {[inspector2.lastname, inspector2.name, inspector2.fathername]
                          .filter(Boolean)
                          .join(" ")}
                      </span>
                      {inspector2.position && (
                        <span className="checkup-add__selected-meta"> · {inspector2.position}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="checkup-add__link"
                      onClick={clearInspector2}
                    >
                      Тозалаш
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      className="checkup-add__search-input"
                      placeholder="ФИО бўйича қидиринг (камида 2 та белги)..."
                      value={inspector2Search}
                      onChange={(e) => setInspector2Search(e.target.value)}
                      disabled={!isOnline}
                    />
                    {!isOnline && (
                      <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                        Офлайн режимда қидириш мумкин эмас.
                      </div>
                    )}
                    {isOnline && inspector2Search.trim().length >= 2 && (
                      <div className="checkup-add__dropdown">
                        {inspector2Loading ? (
                          <div className="checkup-add__dropdown-empty">Қидирилмоқда...</div>
                        ) : inspector2Results.length === 0 ? (
                          <div className="checkup-add__dropdown-empty">Ҳеч нарса топилмади</div>
                        ) : (
                          inspector2Results.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              className="checkup-add__dropdown-item"
                              onClick={() => selectInspector2(u)}
                            >
                              <div className="checkup-add__dropdown-name">
                                {[u.lastname, u.name, u.fathername].filter(Boolean).join(" ")}
                              </div>
                              {u.position && (
                                <div className="checkup-add__dropdown-meta">{u.position}</div>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* 6) Авариявий ҳолат */}
          <div className="checkup-add__block">
            <label className="checkup-add__emergency">
              <input
                type="checkbox"
                checked={isEmergency}
                onChange={(e) => setIsEmergency(e.target.checked)}
              />
              <span>
                Авариявий ҳолат
              </span>
            </label>
          </div>

          {/* 7) Кўрик бандлари */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">Кўрик бандлари</div>

            <div className="checkup-add__check-summary">
              <span>{checklistAnswered}/{activeCheckItems.length} тўлдирилган</span>
              <span>{checklistNoCount} та муаммо</span>
              <span>{missingReasonCount} та сабаб керак</span>
              <span>{yesCommentCount} та изоҳ</span>
            </div>

            <div className="checkup-add__items">
              {activeCheckItems.map(([key, label]) => {
                const v = answers[key] || "yes";
                const isYes = v === "yes";
                const isNo  = v === "no";
                const isNa  = v === "na";
                return (
                  <div
                    key={key}
                    className={
                      "checkup-add__item " + (isNo ? "checkup-add__item--no" : "")
                    }
                  >
                    <div className="checkup-add__item-head">
                      <div className="checkup-add__item-label">{label}</div>

                      <div className="checkup-add__toggle">
                        <button
                          type="button"
                          aria-label="Қониқарли"
                          title="Қониқарли"
                          className={"checkup-add__toggle-btn checkup-add__toggle-btn--yes " + (isYes ? "is-active" : "")}
                          onClick={() => setAnswer(key, "yes")}
                        >
                          ✓
                        </button>

                        <button
                          type="button"
                          aria-label="Қониқарсиз"
                          title="Қониқарсиз"
                          className={"checkup-add__toggle-btn checkup-add__toggle-btn--no " + (isNo ? "is-active" : "")}
                          onClick={() => setAnswer(key, "no")}
                        >
                          ⚠
                        </button>

                        <button
                          type="button"
                          aria-label="Бу таянчга тегишли эмас"
                          title="Бу таянчга тегишли эмас"
                          className={"checkup-add__toggle-btn checkup-add__toggle-btn--na " + (isNa ? "is-active" : "")}
                          onClick={() => setAnswer(key, "na")}
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    {isNo && (
                      <input
                        className="checkup-add__reason"
                        placeholder="Сабаб (нима муаммо?) *"
                        value={reasons[key] || ""}
                        onChange={(e) => setReasons((prev) => ({ ...prev, [key]: e.target.value }))}
                      />
                    )}
                    {isYes && (
                      <input
                        className="checkup-add__reason"
                        placeholder="Изоҳ (ихтиёрий)"
                        value={yesComments[key] || ""}
                        onChange={(e) =>
                          setYesComments((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 8) Фото ва изоҳ + сақлаш */}
          <div className="checkup-add__block">
            <div className="checkup-add__block-title">Фото ва изоҳ</div>
            <textarea
              className="checkup-add__textarea"
              placeholder="Умумий маълумот / кўрик хулосаси..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div style={{ marginTop: 14 }}>
              <div className="checkup-add__block-title">Фото</div>

              <div className="checkup-add__files-row">
                <input
                  key={fileInputKey}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))}
                />
                <button
                  type="button"
                  className="checkup-add__btn"
                  disabled={files.length === 0}
                  onClick={() => setFiles([])}
                >
                  Фотоларни тозалаш
                </button>
              </div>

              {files.length > 0 && (
                <ul className="checkup-add__files-list">
                  {files.map((f) => (
                    <li key={f.name}>{f.name}</li>
                  ))}
                </ul>
              )}
            </div>

            {apiError && (
              <div className="checkup-add__alert checkup-add__alert--error">{apiError}</div>
            )}
            {success && (
              <div className="checkup-add__alert checkup-add__alert--success">{success}</div>
            )}

            <div className="checkup-add__actions">
              <button
                type="button"
                className="checkup-add__submit"
                disabled={!selectedPole || saving}
                onClick={saveCheckup}
              >
                {saving
                  ? "Сақланмоқда..."
                  : isOnline
                  ? "Кўрикни сақлаш"
                  : "Офлайн сақлаш (навбатга қўйиш)"}
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
